// src/api/centralNotificationsApi.ts
// RTK Query API for Central's cross-suite notification endpoints.
//
// Types are imported from @levelup/central-notifications shared package.
// RTK Query API slice stays local — it depends on CRM's Redux store shape.

import { createApi } from '@reduxjs/toolkit/query/react';
import { createCentralBaseQuery } from './centralBaseQuery';

// ============================================================
// Types — re-exported from shared package
// ============================================================

export type {
  AppMetadata,
  CentralNotification,
  GroupedNotification,
  InboxItem,
  GetInboxParams,
  InboxResponse,
  GroupedInboxResponse,
  GetGroupParams,
  GroupDetailResponse,
  MarkGroupReadParams,
  MarkGroupReadResponse,
  UnreadCountParams,
  UnreadCountResponse,
  MarkReadResponse,
  MarkBulkReadParams,
  MarkBulkReadResponse,
  NotificationChannel,
  ChannelConfig,
  NotificationCategoryDefinition,
  CategoriesResponse,
  ChannelPreferenceState,
  CategoryPreference,
  PreferencesResponse,
  UpdateCategoryResponse,
  UpdateCategoryParams,
} from '@levelup/central-notifications';

import type {
  GetInboxParams,
  InboxResponse,
  GroupedInboxResponse,
  GetGroupParams,
  GroupDetailResponse,
  MarkGroupReadParams,
  MarkGroupReadResponse,
  UnreadCountParams,
  MarkReadResponse,
  MarkBulkReadParams,
  MarkBulkReadResponse,
  CategoriesResponse,
  PreferencesResponse,
  UpdateCategoryResponse,
  UpdateCategoryParams,
  NotificationChannel,
} from '@levelup/central-notifications';
// Canonical inbox unread-count shape ({ ok, total, byApp }) — Central does not
// return the shared `UnreadCountResponse.unreadCount`. Used by the dormant
// getUnreadCount cache (kept contract-correct for PR-N2's SSE proxy).
import type { InboxUnreadCount } from './notificationInboxTypes';

// ============================================================
// API
// ============================================================

export const centralNotificationsApi = createApi({
  reducerPath: 'centralNotificationsApi',
  baseQuery: createCentralBaseQuery({
    pathPrefix: '/api/notifications',
    prepareExtraHeaders: (headers) => {
      headers.set('Content-Type', 'application/json');
    },
  }),
  tagTypes: ['CentralNotification', 'CentralUnreadCount', 'CentralNotificationGroup', 'CentralNotificationPreferences', 'CentralNotificationCategories'],
  endpoints: (builder) => ({
    /**
     * Get notifications inbox (non-grouped, for backward compatibility)
     * GET /api/notifications/inbox?workspaceId=...&appId=...&cursor=...&limit=...&enriched=true
     */
    getInbox: builder.query<InboxResponse, GetInboxParams>({
      query: ({ workspaceId, appId, cursor, limit, enriched }) => ({
        url: '/inbox',
        params: {
          workspaceId,
          ...(appId && { appId }),
          ...(cursor && { cursor }),
          ...(limit && { limit }),
          ...(enriched && { enriched: 'true' }),
        },
      }),
      // Merge paginated results
      serializeQueryArgs: ({ queryArgs }) => {
        const { cursor, ...rest } = queryArgs;
        return rest;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.cursor) {
          // Appending more results
          return {
            ...newItems,
            notifications: [...currentCache.notifications, ...newItems.notifications],
          };
        }
        // Fresh fetch
        return newItems;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg?.cursor !== previousArg?.cursor;
      },
      providesTags: (_result, _error, arg) => [
        { type: 'CentralNotification', id: arg.appId ? `APP_${arg.appId}` : 'WORKSPACE' },
      ],
    }),

    /**
     * Get grouped notifications inbox
     * GET /api/notifications/inbox?workspaceId=...&grouped=true&enriched=true
     */
    getGroupedInbox: builder.query<GroupedInboxResponse, GetInboxParams>({
      query: ({ workspaceId, appId, cursor, limit, enriched = true }) => ({
        url: '/inbox',
        params: {
          workspaceId,
          grouped: 'true',
          enriched: enriched ? 'true' : undefined,
          ...(appId && { appId }),
          ...(cursor && { cursor }),
          ...(limit && { limit }),
        },
      }),
      // Merge paginated results
      serializeQueryArgs: ({ queryArgs }) => {
        const { cursor, ...rest } = queryArgs;
        return rest;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.cursor) {
          return {
            ...newItems,
            items: [...currentCache.items, ...newItems.items],
          };
        }
        return newItems;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg?.cursor !== previousArg?.cursor;
      },
      providesTags: (_result, _error, arg) => [
        { type: 'CentralNotification', id: arg.appId ? `APP_${arg.appId}` : 'WORKSPACE' },
        { type: 'CentralNotificationGroup', id: 'LIST' },
      ],
    }),

    /**
     * Get notifications within a specific group
     * GET /api/notifications/inbox/groups/:correlationId?workspaceId=...
     */
    getGroupNotifications: builder.query<GroupDetailResponse, GetGroupParams>({
      query: ({ workspaceId, correlationId }) => ({
        url: `/inbox/groups/${encodeURIComponent(correlationId)}`,
        params: { workspaceId },
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'CentralNotificationGroup', id: arg.correlationId },
      ],
    }),

    /**
     * Get unread count
     * GET /api/notifications/inbox/unread-count?workspaceId=...&appId=...
     */
    getUnreadCount: builder.query<InboxUnreadCount, UnreadCountParams>({
      query: ({ workspaceId, appId }) => ({
        url: '/inbox/unread-count',
        params: {
          workspaceId,
          ...(appId && { appId }),
        },
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'CentralUnreadCount', id: arg.appId ? `APP_${arg.appId}` : 'WORKSPACE' },
        { type: 'CentralUnreadCount', id: 'TOTAL' },
      ],
    }),

    /**
     * Mark single notification as read
     * PATCH /api/notifications/inbox/:id/read
     */
    markNotificationRead: builder.mutation<MarkReadResponse, string>({
      query: (id) => ({
        url: `/inbox/${id}/read`,
        method: 'PATCH',
      }),
      invalidatesTags: [
        { type: 'CentralNotification', id: 'WORKSPACE' },
        { type: 'CentralUnreadCount', id: 'TOTAL' },
      ],
      // Optimistic update
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate all inbox caches to refresh read state
        } catch {
          // Error handled by RTK Query
        }
      },
    }),

    /**
     * Mark multiple notifications as read (or all in context)
     * POST /api/notifications/inbox/mark-read
     */
    markBulkRead: builder.mutation<MarkBulkReadResponse, MarkBulkReadParams>({
      query: ({ workspaceId, appId, notificationIds }) => ({
        url: '/inbox/mark-read',
        method: 'POST',
        body: {
          workspaceId,
          ...(appId && { appId }),
          ...(notificationIds && { notificationIds }),
        },
      }),
      invalidatesTags: [
        { type: 'CentralNotification', id: 'WORKSPACE' },
        { type: 'CentralUnreadCount', id: 'TOTAL' },
        { type: 'CentralNotificationGroup', id: 'LIST' },
      ],
    }),

    /**
     * Mark all notifications in a group as read
     * POST /api/notifications/inbox/groups/:correlationId/mark-read
     */
    markGroupRead: builder.mutation<MarkGroupReadResponse, MarkGroupReadParams>({
      query: ({ workspaceId, correlationId }) => ({
        url: `/inbox/groups/${encodeURIComponent(correlationId)}/mark-read`,
        method: 'POST',
        body: { workspaceId },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'CentralNotification', id: 'WORKSPACE' },
        { type: 'CentralUnreadCount', id: 'TOTAL' },
        { type: 'CentralNotificationGroup', id: arg.correlationId },
        { type: 'CentralNotificationGroup', id: 'LIST' },
      ],
    }),

    // ============================================================
    // Notification Preferences Endpoints
    // ============================================================

    /**
     * Get notification category definitions
     * GET /api/notifications/preferences/categories
     */
    getNotificationCategories: builder.query<CategoriesResponse, void>({
      query: () => '/preferences/categories',
      providesTags: ['CentralNotificationCategories'],
    }),

    /**
     * Get user's notification preferences
     * GET /api/notifications/preferences
     */
    getCentralNotificationPreferences: builder.query<PreferencesResponse, void>({
      query: () => '/preferences',
      providesTags: ['CentralNotificationPreferences'],
    }),

    /**
     * Update a single category's channel preferences
     * PATCH /api/notifications/preferences/:category
     */
    updateCategoryPreference: builder.mutation<UpdateCategoryResponse, UpdateCategoryParams>({
      query: ({ category, channels }) => ({
        url: `/preferences/${encodeURIComponent(category)}`,
        method: 'PATCH',
        body: { channels },
      }),
      // Optimistic update
      async onQueryStarted({ category, channels }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          centralNotificationsApi.util.updateQueryData(
            'getCentralNotificationPreferences',
            undefined,
            (draft) => {
              const pref = draft.preferences.find((p) => p.category === category);
              if (pref) {
                for (const [ch, enabled] of Object.entries(channels)) {
                  const channel = ch as NotificationChannel;
                  if (channel in pref.channels && typeof enabled === 'boolean') {
                    pref.channels[channel].enabled = enabled;
                  }
                }
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ['CentralNotificationPreferences'],
    }),
  }),
});

export const {
  useGetInboxQuery,
  useGetGroupedInboxQuery,
  useGetGroupNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkNotificationReadMutation,
  useMarkBulkReadMutation,
  useMarkGroupReadMutation,
  useGetNotificationCategoriesQuery,
  useGetCentralNotificationPreferencesQuery,
  useUpdateCategoryPreferenceMutation,
} = centralNotificationsApi;
